import { RefObject, useEffect, useState } from 'react';

interface ScrollIndicatorState {
  showLeftShadow: boolean;
  showRightShadow: boolean;
  isAtStart: boolean;
  isAtEnd: boolean;
}

export function useScrollIndicator(
  scrollRef: RefObject<HTMLDivElement>
): ScrollIndicatorState {
  const [state, setState] = useState<ScrollIndicatorState>({
    showLeftShadow: false,
    showRightShadow: false,
    isAtStart: true,
    isAtEnd: false
  });

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) < 1;

      setState({
        showLeftShadow: !isAtStart,
        showRightShadow: !isAtEnd,
        isAtStart,
        isAtEnd
      });
    };

    // Check initial state
    checkScroll();

    // Add scroll listener
    element.addEventListener('scroll', checkScroll);

    // Add resize observer to handle window/container size changes
    // Graceful degradation for environments without ResizeObserver (like tests)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(element);
    }

    return () => {
      element.removeEventListener('scroll', checkScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [scrollRef]);

  return state;
}
